"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Great_Vibes, Cormorant_Garamond } from "next/font/google";
import GalleryQrOverlay from "@/app/components/GalleryQrOverlay";
import MediaQrCode from "@/app/components/MediaQrCode";
import SharePanel from "@/app/components/SharePanel";
import SessionBackLink from "@/app/components/SessionBackLink";
import { getAlbumShortPath, getVideoShortPath } from "@/app/lib/shareLinks";
import {
  COUPLE_PASSWORD,
  getSession,
  SUPERADMIN_PASSWORD,
  type PortalSession,
} from "@/app/lib/weddingPortal";

const greatVibes = Great_Vibes({
  subsets: ["latin"],
  weight: "400",
});

const cormorant = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

type VideoItem = {
  id: string;
  url: string;
  title: string;
  createdAt?: string;
  mimeType?: string;
  sharePath?: string;
  downloadPath?: string;
  fullscreenPath?: string;
  qrPath?: string;
  publicPagePath?: string;
  originalName?: string;
  sizeBytes?: number;
};

type ApiMediaItem = VideoItem & {
  type: "image" | "video";
};

const GALLERY_REFRESH_MS = 10000;

type GallerySort = "newest" | "oldest" | "title" | "largest";

function getTimestamp(item: VideoItem) {
  return item.createdAt ? new Date(item.createdAt).getTime() || 0 : 0;
}

function sortNewestFirst(items: VideoItem[]) {
  return [...items].sort((a, b) => getTimestamp(b) - getTimestamp(a));
}

function sortVideos(items: VideoItem[], sortOrder: GallerySort) {
  return [...items].sort((a, b) => {
    if (sortOrder === "oldest") return getTimestamp(a) - getTimestamp(b);
    if (sortOrder === "title") return a.title.localeCompare(b.title);
    if (sortOrder === "largest") return (b.sizeBytes ?? 0) - (a.sizeBytes ?? 0);
    return getTimestamp(b) - getTimestamp(a);
  });
}

function formatBytes(bytes?: number) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getInitialFocusId() {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("focus");
}

function getVideoSharePath(item: VideoItem) {
  return item.sharePath ?? getVideoShortPath(item.id);
}

function getVideoDownloadPath(item: VideoItem) {
  return item.downloadPath ?? item.url;
}

function getVideoFullscreenPath(item: VideoItem) {
  return item.fullscreenPath ?? item.url;
}

function getVideoQrPath(item: VideoItem) {
  return item.qrPath ?? getVideoSharePath(item);
}

function getTokenFromSharePath(path?: string) {
  const match = path?.match(/\/m\/([^/?#]+)/);
  return match?.[1] ?? null;
}

function getVideoPublicPagePath(item: VideoItem) {
  const token = getTokenFromSharePath(item.sharePath);
  return item.publicPagePath ?? (token ? `/wedding/videos/${token}` : getVideoSharePath(item));
}

function getDeletePassword(session: PortalSession | null) {
  if (session?.role === "superadmin") return SUPERADMIN_PASSWORD;
  if (session?.role === "couple") return COUPLE_PASSWORD;
  return "";
}

function getUploadedVideos(items: ApiMediaItem[] = []) {
  return sortNewestFirst(items.filter((item) => item.type === "video"));
}

function formatDate(value?: string) {
  if (!value) return "Wedding Memory";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "Wedding Memory";
  }
}

function ActionLink({
  href,
  children,
  primary = false,
}: {
  href: string;
  children: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <a
      href={href}
      className={
        primary
          ? "inline-flex min-h-12 items-center justify-center rounded-full bg-[#1f4e79] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-sm transition hover:bg-[#173d61]"
          : "inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
      }
    >
      {children}
    </a>
  );
}

export default function VideosPage() {
  const [items, setItems] = useState<VideoItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [focusId] = useState(getInitialFocusId);
  const [selectedId, setSelectedId] = useState<string | null>(focusId);
  const [playingVideoIds, setPlayingVideoIds] = useState<Set<string>>(() => new Set());
  const [portalSession, setPortalSession] = useState<PortalSession | null>(null);
  const [sortOrder, setSortOrder] = useState<GallerySort>("newest");
  const [manageStatus, setManageStatus] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    setPortalSession(getSession());

    function handleStorage() {
      setPortalSession(getSession());
    }

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  function setCardVideoPlaying(id: string, isPlaying: boolean) {
    setPlayingVideoIds((current) => {
      const next = new Set(current);
      if (isPlaying) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  useEffect(() => {
    let active = true;

    async function loadVideos() {
      try {
        const response = await fetch("/api/media", { cache: "no-store" });
        const json = (await response.json()) as { items?: ApiMediaItem[] };

        if (!active) return;

        setItems(getUploadedVideos(json.items));
        setLastUpdatedAt(new Date().toISOString());
      } catch (error) {
        console.error(error);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void loadVideos();
    const intervalId = window.setInterval(loadVideos, GALLERY_REFRESH_MS);
    const refreshOnFocus = () => void loadVideos();
    const refreshOnVisibility = () => {
      if (document.visibilityState === "visible") {
        void loadVideos();
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnVisibility);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
    };
  }, []);

  useEffect(() => {
    if (focusId && items.some((item) => item.id === focusId)) {
      setSelectedId(focusId);
    }
  }, [focusId, items]);

  useEffect(() => {
    if (selectedId && !items.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, selectedId]);

  const selectedVideo = useMemo(
    () => (selectedId ? items.find((item) => item.id === selectedId) ?? null : null),
    [items, selectedId]
  );
  const visibleItems = useMemo(() => sortVideos(items, sortOrder), [items, sortOrder]);
  const selectedPublicPageLink = selectedVideo ? getVideoPublicPagePath(selectedVideo) : "";

  async function deleteVideo(item: VideoItem) {
    if (!portalSession) return;

    const confirmed = window.confirm(`Delete "${item.title}" permanently?`);
    if (!confirmed) return;

    setDeletingId(item.id);
    setManageStatus("Deleting video...");

    try {
      const response = await fetch("/api/media/manage", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: item.id,
          password: getDeletePassword(portalSession),
        }),
      });
      const json = (await response.json()) as {
        items?: ApiMediaItem[];
        error?: string;
      };

      if (!response.ok || !json.items) {
        throw new Error(json.error || "Unable to delete this video.");
      }

      setItems(getUploadedVideos(json.items));
      if (selectedId === item.id) setSelectedId(null);
      setManageStatus("Video deleted.");
    } catch (error) {
      console.error(error);
      setManageStatus(error instanceof Error ? error.message : "Unable to delete this video.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <main className={`min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(31,78,121,0.10),transparent_34%),linear-gradient(180deg,#fbfaf7_0%,#f1eee6_100%)] px-4 py-6 text-slate-800 sm:px-6 lg:px-8 ${cormorant.className}`}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <SessionBackLink />

          <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-[#8c8c86]">
            John &amp; Maxine
          </p>

          <div className="w-20" />
        </div>

        <section className="relative mb-8 overflow-hidden rounded-[2.25rem] border border-[#decf9b] bg-white/85 px-5 py-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] sm:px-10 lg:px-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(202,162,58,0.18),transparent_24%),radial-gradient(circle_at_84%_18%,rgba(31,78,121,0.14),transparent_26%)]" />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[#8c8c86]">
              Newest First · Live Updates
            </p>
            <h1 className={`${greatVibes.className} wedding-gold-halo mt-4 text-6xl leading-none text-[#1f4e79] sm:text-7xl lg:text-8xl`}>
              Video Gallery
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-[#686861] sm:text-lg">
              Watch the newest wedding videos, preview them inline, download keepsakes, share secure links, open fullscreen, or scan a QR code.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="rounded-3xl border border-[#ead8a0] bg-[#fffaf0]/80 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">Videos</p>
                <p className="mt-1 text-3xl font-semibold text-[#1f3f6d]">{items.length}</p>
              </div>
              <div className="rounded-3xl border border-[#ead8a0] bg-[#fffaf0]/80 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">Refresh</p>
                <p className="mt-1 text-lg font-semibold text-[#1f3f6d]">Automatic</p>
              </div>
              <div className="rounded-3xl border border-[#ead8a0] bg-[#fffaf0]/80 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">Updated</p>
                <p className="mt-1 text-lg font-semibold text-[#1f3f6d]">
                  {lastUpdatedAt ? formatDate(lastUpdatedAt) : "Loading"}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="mb-8">
          <SharePanel title="Videos Album" shortPath={getAlbumShortPath("videos")} />
        </div>

        <section className="mb-8 rounded-[1.75rem] border border-[#decf9b] bg-white/90 p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8c8c86]">
                Video Controls
              </p>
              <p className="mt-2 text-sm leading-6 text-[#686861]">
                Sort videos by date, title, or size. Delete controls appear only when logged in.
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-4">
              {(["newest", "oldest", "title", "largest"] as const).map((nextSort) => (
                <button
                  key={nextSort}
                  type="button"
                  onClick={() => setSortOrder(nextSort)}
                  className={
                    sortOrder === nextSort
                      ? "min-h-11 rounded-full bg-[#1f4e79] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white"
                      : "min-h-11 rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1f4e79]"
                  }
                >
                  {nextSort === "title"
                    ? "A-Z"
                    : nextSort === "largest"
                      ? "Largest"
                      : nextSort}
                </button>
              ))}
            </div>
          </div>

          {portalSession ? (
            <p className="mt-4 rounded-2xl border border-[#ead8a0] bg-[#fffaf0] px-4 py-3 text-sm font-semibold text-[#1f4e79]">
              Manage mode active as {portalSession.role === "superadmin" ? "admin" : "couple"}.
            </p>
          ) : null}

          {manageStatus ? (
            <p className="mt-3 rounded-2xl border border-[#ead8a0] bg-[#fffdfa] px-4 py-3 text-sm text-[#1f4e79]" role="status">
              {manageStatus}
            </p>
          ) : null}
        </section>

        {isLoading ? (
          <div className="rounded-[2rem] border border-[#decf9b] bg-white px-6 py-12 text-center shadow-sm">
            <p className="text-lg text-[#686861]">Loading the latest wedding videos...</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="rounded-[2rem] border border-[#decf9b] bg-white px-6 py-12 text-center shadow-sm">
            <p className="text-lg text-[#686861]">No videos have been uploaded yet.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {visibleItems.map((item) => {
              const shareLink = getVideoSharePath(item);
              const qrLink = getVideoQrPath(item);
              const publicPageLink = getVideoPublicPagePath(item);

              return (
                <article
                  key={item.id}
                  className="group overflow-hidden rounded-[2rem] border border-[#decf9b] bg-white shadow-[0_18px_55px_rgba(15,23,42,0.09)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_26px_70px_rgba(15,23,42,0.13)]"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-black">
                    <button
                      type="button"
                      onClick={() => setSelectedId(item.id)}
                      className="absolute inset-0 block w-full text-left"
                      aria-label={`View full video ${item.title}`}
                    >
                      <video
                        src={item.url}
                        className="h-full w-full object-cover opacity-95 transition duration-500 group-hover:scale-[1.04]"
                        muted
                        playsInline
                        preload="metadata"
                        onPlay={() => setCardVideoPlaying(item.id, true)}
                        onPause={() => setCardVideoPlaying(item.id, false)}
                        onEnded={() => setCardVideoPlaying(item.id, false)}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="grid h-16 w-16 place-items-center rounded-full border border-white/40 bg-black/35 text-2xl text-white shadow-lg backdrop-blur">
                          ▶
                        </span>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-5 text-white">
                        <p className="text-xs uppercase tracking-[0.22em] text-[#f8dc91]">Wedding Video</p>
                        <h2 className="mt-2 line-clamp-2 text-2xl font-semibold">{item.title}</h2>
                        <p className="mt-1 text-sm text-white/80">
                          {formatDate(item.createdAt)}
                          {item.sizeBytes ? ` · ${formatBytes(item.sizeBytes)}` : ""}
                        </p>
                      </div>
                    </button>

                    <GalleryQrOverlay
                      path={publicPageLink}
                      mediaType="video"
                      suspendCycle={playingVideoIds.has(item.id)}
                    />
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setSelectedId(item.id)}
                        className="inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                      >
                        View Full Video
                      </button>
                      <ActionLink href={getVideoDownloadPath(item)} primary>
                        Download
                      </ActionLink>
                      <ActionLink href={shareLink}>Share</ActionLink>
                      <ActionLink href={getVideoFullscreenPath(item)}>Fullscreen</ActionLink>
                      {portalSession ? (
                        <button
                          type="button"
                          onClick={() => void deleteVideo(item)}
                          disabled={deletingId === item.id}
                          className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#a85249] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:bg-[#7a3029] disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
                        >
                          {deletingId === item.id ? "Deleting..." : "Delete Video"}
                        </button>
                      ) : null}
                    </div>

                    <div className="grid gap-4 rounded-[1.5rem] border border-[#ead8a0] bg-[#fcfbf8] p-4 sm:grid-cols-[auto_1fr] sm:items-center">
                      <div className="mx-auto rounded-2xl bg-[#fffaf0] p-2 shadow-inner">
                        <MediaQrCode path={publicPageLink} size={120} />
                      </div>
                      <div className="text-center sm:text-left">
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8c8c86]">QR Code</p>
                        <p className="mt-2 text-sm leading-6 text-[#686861]">Scan to download this video.</p>
                        <a
                          href={qrLink}
                          className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                        >
                          Open QR Code
                        </a>
                      </div>
                    </div>

                    <SharePanel title={item.title} shortPath={shareLink} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {selectedVideo ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-3 sm:items-center sm:p-4"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="relative my-3 max-h-[calc(100dvh-1.5rem)] w-full max-w-6xl overflow-y-auto rounded-[1.5rem] border border-[#decf9b] bg-white shadow-2xl sm:my-0 sm:rounded-[2rem]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-[#ead8a0] px-4 py-4 sm:px-6">
              <div className="text-left">
                <p className="text-xs uppercase tracking-[0.22em] text-[#8c8c86]">John &amp; Maxine</p>
                <p className="text-lg font-semibold text-[#1f3f6d]">{selectedVideo.title}</p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-4 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
              >
                Close
              </button>
            </div>

            <div className="grid gap-4 bg-black p-4 lg:grid-cols-[1fr_260px] sm:p-6">
              <video
                src={selectedVideo.url}
                controls
                playsInline
                preload="metadata"
                className="mx-auto max-h-[70vh] w-full rounded-[1.25rem] bg-black"
              />

              <aside className="flex flex-col items-center justify-center rounded-[1.5rem] border border-[#ead8a0] bg-[#fffaf0] p-4 text-center">
                <MediaQrCode path={selectedPublicPageLink} size={190} />
                <p className="mt-3 text-sm font-semibold leading-6 text-[#1f3f6d]">
                  Scan to download this video
                </p>
                <a
                  href={selectedPublicPageLink}
                  className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-[#caa23a]/60 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                >
                  Open Video Page
                </a>
              </aside>
            </div>

            <div className="grid gap-4 border-t border-[#ead8a0] px-4 py-4 lg:grid-cols-[1fr_auto] lg:items-center sm:px-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#fffaf0] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#1f4e79]"
                >
                  Back to Gallery
                </button>
                <ActionLink href={getVideoDownloadPath(selectedVideo)} primary>
                  Download
                </ActionLink>
                <ActionLink href={getVideoSharePath(selectedVideo)}>Share</ActionLink>
                <ActionLink href={getVideoFullscreenPath(selectedVideo)}>Fullscreen</ActionLink>
                <ActionLink href={getVideoQrPath(selectedVideo)}>QR Code</ActionLink>
                {portalSession ? (
                  <button
                    type="button"
                    onClick={() => void deleteVideo(selectedVideo)}
                    disabled={deletingId === selectedVideo.id}
                    className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#a85249] px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {deletingId === selectedVideo.id ? "Deleting..." : "Delete"}
                  </button>
                ) : null}
              </div>

              <div className="mx-auto rounded-2xl border border-[#ead8a0] bg-[#fffaf0] p-2 lg:hidden">
                <MediaQrCode path={selectedPublicPageLink} size={120} />
              </div>
            </div>

            <div className="border-t border-[#ead8a0] px-4 py-4 sm:px-6">
              <SharePanel
                title={selectedVideo.title}
                shortPath={getVideoSharePath(selectedVideo)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
